import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import inventory from "@/api/inventory";
import inventoryPdf, { pdfErrorMessage } from "@/api/inventoryPdf";
import PdfExportButton from "./PdfExportButton";

const COLS = [
  { key: "canonical_name",       label: "Usuario",        numeric: false },
  { key: "total_tx",             label: "Total Tx",       numeric: true  },
  { key: "entradas_count",       label: "Entradas",       numeric: true  },
  { key: "salidas_count",        label: "Salidas",        numeric: true  },
  { key: "salida_orden_count",   label: "Salida orden",   numeric: true  },
  { key: "devoluciones_count",   label: "Devoluciones",   numeric: true  },
  { key: "ajustes_count",        label: "Ajustes",        numeric: true  },
  { key: "tx_per_day_avg",       label: "Tx/día prom",    numeric: true  },
  { key: "costo_total_movido",   label: "Costo total",    numeric: true  },
];

function fmtNum(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US"); }
function fmtMoney(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }); }

export default function InventoryUserMovementsTable({
  filters,
  testId = "user-movements-table",
  apiOverride,
  hideExport = false,
}) {
  const { t } = useTranslation();
  const api = apiOverride || inventory;
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [sortBy, setSortBy] = useState("total_tx");
  const [sortDir, setSortDir] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], total: 0, span_days: null });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => { setPage(1); }, [filters?.preset, filters?.date_from, filters?.date_to, filters?.include_non_warehouse, search]);
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getUserMovements(filters, { page, pageSize, sortBy, sortDir, search })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(t("inventory.errors.userMovementsLoad", { defaultValue: "No se pudo cargar movimientos por usuario" }));
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, pageSize, sortBy, sortDir, search, t]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / pageSize)), [data.total, pageSize]);

  const handleSort = (k) => {
    if (sortBy === k) setSortDir((d) => d === "desc" ? "asc" : "desc");
    else { setSortBy(k); setSortDir(COLS.find((c) => c.key === k)?.numeric ? "desc" : "asc"); }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await api.downloadUserMovementsExport(filters, { sortBy, sortDir, search });
      toast.success(t("inventory.export.success", { defaultValue: "Excel descargado" }));
    } catch (e) {
      toast.error(t("inventory.export.error", { defaultValue: "Fallo al exportar Excel" }));
      console.error(e);
    } finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await inventoryPdf.userMovements(filters, {
        sortBy,
        sortDir,
        search,
        include_non_warehouse: filters?.include_non_warehouse,
      });
      toast.success(t("inventory.exportPdf.success", { defaultValue: "PDF descargado" }));
    } catch (e) {
      toast.error(pdfErrorMessage(e, t));
      console.error(e);
    } finally { setExportingPdf(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t("inventory.searchUser", { defaultValue: "Buscar usuario..." })}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-7 h-8 text-sm"
            data-testid="user-search"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {loading || data.span_days === null || data.span_days === undefined ? (
            <Skeleton className="h-3.5 w-20" data-testid="user-span-skeleton" />
          ) : (
            <span data-testid="user-span-info">{t("inventory.spanDays", { count: data.span_days, defaultValue: `Rango: ${data.span_days} días` })}</span>
          )}
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading} data-testid="user-export-btn" className={hideExport ? "hidden" : ""}>
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            {t("inventory.exportExcel", { defaultValue: "Exportar Excel" })}
          </Button>
          <PdfExportButton
            onClick={handleExportPdf}
            exporting={exportingPdf}
            disabled={loading}
            hidden={hideExport}
            testId="user-pdf-btn"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {COLS.map((c) => (
                  <TableHead
                    key={c.key}
                    className={`text-xs cursor-pointer ${c.numeric ? "text-right" : ""}`}
                    onClick={() => handleSort(c.key)}
                    data-testid={`user-th-${c.key}`}
                  >
                    {c.label}{sortBy === c.key ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && data.items.length === 0
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>{COLS.map((c) => <TableCell key={c.key}><Skeleton className="h-3.5 w-full" /></TableCell>)}</TableRow>
                  ))
                : data.items.length === 0
                  ? <TableRow><TableCell colSpan={COLS.length} className="text-center text-muted-foreground py-8">{t("inventory.empty", { defaultValue: "Sin resultados" })}</TableCell></TableRow>
                  : data.items.map((row) => (
                    <TableRow key={row.canonical_id || row.canonical_name} data-testid={`user-row-${row.canonical_id || "x"}`}>
                      <TableCell className="text-xs">{row.canonical_name}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{fmtNum(row.total_tx)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-emerald-600">{fmtNum(row.entradas_count)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-rose-600">{fmtNum(row.salidas_count)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-rose-500">{fmtNum(row.salida_orden_count)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-amber-600">{fmtNum(row.devoluciones_count)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-violet-600">{fmtNum(row.ajustes_count)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{Number(row.tx_per_day_avg).toFixed(1)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{fmtMoney(row.costo_total_movido)}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div data-testid="user-total-count">
          {loading && data.items.length === 0
            ? <Skeleton className="h-3.5 w-24 inline-block align-middle" data-testid="user-total-skeleton" />
            : <>{(data.total || 0).toLocaleString("en-US")} {t("inventory.users", { defaultValue: "usuarios" })}</>}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums px-2">{page} / {totalPages}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
